import { apiFetch } from "@/lib/api";
import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Camera, Upload, Languages, Volume2, Loader2, History,
  LogOut, ChevronDown, ImagePlus, X, RotateCcw, Sparkles,
  BookOpen, Trophy, Bookmark, Shield
} from "lucide-react";
import CameraCapture from "@/components/CameraCapture";
import TranslationResult from "@/components/TranslationResult";


const LANGUAGES = [
  "Afrikaans", "Albanian", "Amharic", "Arabic", "Armenian", "Assamese", "Aymara", "Azerbaijani", "Bambara", "Basque",
  "Belarusian", "Bengali", "Bhojpuri", "Bosnian", "Bulgarian", "Catalan", "Cebuano", "Chichewa", "Chinese (Simplified)",
  "Chinese (Traditional)", "Corsican", "Croatian", "Czech", "Danish", "Dhivehi", "Dogri", "Dutch", "English",
  "Esperanto", "Estonian", "Ewe", "Filipino", "Finnish", "French", "Frisian", "Galician", "Georgian", "German",
  "Greek", "Guarani", "Gujarati", "Haitian Creole", "Hausa", "Hawaiian", "Hebrew", "Hindi", "Hmong", "Hungarian",
  "Icelandic", "Igbo", "Ilocano", "Indonesian", "Irish", "Italian", "Japanese", "Javanese", "Kannada", "Kazakh",
  "Khmer", "Kinyarwanda", "Konkani", "Korean", "Krio", "Kurdish (Kurmanji)", "Kurdish (Sorani)", "Kyrgyz", "Lao",
  "Latin", "Latvian", "Lingala", "Lithuanian", "Luganda", "Luxembourgish", "Macedonian", "Maithili", "Malagasy",
  "Malay", "Malayalam", "Maltese", "Maori", "Marathi", "Meiteilon (Manipuri)", "Mizo", "Mongolian", "Myanmar (Burmese)",
  "Nepali", "Norwegian", "Odia (Oriya)", "Oromo", "Pashto", "Persian", "Polish", "Portuguese", "Punjabi", "Quechua",
  "Romanian", "Russian", "Samoan", "Sanskrit", "Scots Gaelic", "Sepedi", "Serbian", "Sesotho", "Shona", "Sindhi",
  "Sinhala", "Slovak", "Slovenian", "Somali", "Spanish", "Sundanese", "Swahili", "Swedish", "Tajik", "Tamil", "Tatar",
  "Telugu", "Thai", "Tigrinya", "Tsonga", "Turkish", "Turkmen", "Twi", "Ukrainian", "Urdu", "Uyghur", "Uzbek",
  "Vietnamese", "Welsh", "Xhosa", "Yiddish", "Yoruba", "Zulu"
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [mode, setMode] = useState(null); // null | 'camera' | 'upload'
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [targetLanguage, setTargetLanguage] = useState("Spanish");
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationResult, setTranslationResult] = useState(null);

  const handleImageUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.match(/image\/(jpeg|png|webp)/)) {
      toast.error("Please upload a JPEG, PNG, or WEBP image");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result;
      setImagePreview(base64);
      setImageBase64(base64.split(",")[1]);
      setMode("upload");
      setTranslationResult(null);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleCameraCapture = useCallback((base64Data) => {
    setImagePreview(`data:image/jpeg;base64,${base64Data}`);
    setImageBase64(base64Data);
    setMode("upload");
    setTranslationResult(null);
  }, []);

  const handleTranslate = async () => {
    if (!imageBase64) {
      toast.error("Please capture or upload an image first");
      return;
    }

    setIsTranslating(true);
    try {
      const response = await apiFetch(`/api/translate/image`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },

        body: JSON.stringify({
          image_base64: imageBase64,
          target_language: targetLanguage,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Translation failed");
      }

      const data = await response.json();
      const hasText = data.extracted_text && data.extracted_text.trim();
      const hasObjects = data.translated_objects && data.translated_objects.length > 0;
      if (!hasText && !hasObjects) {
        toast.warning("No text or objects were detected. Try a clearer photo.");
        setTranslationResult(null);
      } else {
        setTranslationResult(data);
        toast.success("Translation complete!");
      }
    } catch (err) {
      toast.error(err.message || "Translation failed");
    } finally {
      setIsTranslating(false);
    }
  };

  const handleReset = () => {
    setMode(null);
    setImagePreview(null);
    setImageBase64(null);
    setTranslationResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Top Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/dashboard")}>
            <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center">
              <Camera className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-extrabold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
              LingoLens
            </span>
          </div>

          <div className="flex items-center gap-1 sm:gap-3">
            <Button
              data-testid="flashcards-btn"
              variant="ghost"
              onClick={() => navigate("/flashcards")}
              className="rounded-full text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 font-semibold hidden sm:flex"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Flashcards
            </Button>
            <Button
              data-testid="quiz-btn"
              variant="ghost"
              onClick={() => navigate("/quiz")}
              className="rounded-full text-slate-600 hover:text-yellow-600 hover:bg-yellow-50 font-semibold hidden sm:flex"
            >
              <Trophy className="w-4 h-4 mr-2" />
              Quiz
            </Button>
            <Button
              data-testid="history-btn"
              variant="ghost"
              onClick={() => navigate("/history")}
              className="rounded-full text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 font-semibold"
            >
              <History className="w-4 h-4 mr-2" />
              History
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="user-menu-btn" variant="ghost" className="rounded-full flex items-center gap-2 hover:bg-slate-50 px-2">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-indigo-100 text-indigo-700 text-sm font-bold">
                      {user?.name?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-semibold text-slate-700 hidden md:block">{user?.name}</span>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 rounded-xl">
                <DropdownMenuItem className="text-sm text-slate-500 cursor-default">
                  {user?.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="admin-link"
                  onClick={() => navigate("/admin")}
                  className="cursor-pointer"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Admin Panel
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="logout-btn"
                  onClick={handleLogout}
                  className="text-red-600 cursor-pointer"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8">
        {/* Welcome banner */}
        <div className="mb-8 animate-slide-up">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Welcome back, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-slate-500 mt-1">Capture or upload an image to start translating</p>
        </div>

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Image Input Area - Large block */}
          <div className="md:col-span-7 bg-white rounded-3xl border border-slate-100 shadow-xl shadow-indigo-100/20 p-6 md:p-8 animate-slide-up delay-100">
            {mode === "camera" ? (
              <CameraCapture onCapture={handleCameraCapture} onCancel={() => setMode(null)} />
            ) : imagePreview ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-extrabold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
                    Captured Image
                  </h2>
                  <Button
                    data-testid="reset-image-btn"
                    variant="ghost"
                    size="sm"
                    onClick={handleReset}
                    className="text-slate-500 hover:text-red-500 rounded-full"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                </div>
                <div className="rounded-2xl overflow-hidden border border-slate-100 bg-slate-50">
                  <img
                    src={imagePreview}
                    alt="Captured"
                    className="w-full max-h-[400px] object-contain"
                    data-testid="captured-image"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <h2 className="text-lg font-extrabold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  Choose Input Method
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    data-testid="camera-mode-btn"
                    onClick={() => setMode("camera")}
                    className="group relative overflow-hidden rounded-2xl border-2 border-dashed border-indigo-200 hover:border-indigo-400 bg-indigo-50/50 p-8 text-center transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                  >
                    <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Camera className="w-8 h-8 text-indigo-600" />
                    </div>
                    <p className="font-bold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>Use Camera</p>
                    <p className="text-sm text-slate-500 mt-1">Snap a photo of text</p>
                  </button>

                  <button
                    data-testid="upload-mode-btn"
                    onClick={() => fileInputRef.current?.click()}
                    className="group relative overflow-hidden rounded-2xl border-2 border-dashed border-yellow-200 hover:border-yellow-400 bg-yellow-50/50 p-8 text-center transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                  >
                    <div className="w-16 h-16 rounded-2xl bg-yellow-100 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                      <ImagePlus className="w-8 h-8 text-yellow-700" />
                    </div>
                    <p className="font-bold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>Upload Image</p>
                    <p className="text-sm text-slate-500 mt-1">Choose from your device</p>
                  </button>
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleImageUpload}
              className="hidden"
              data-testid="file-input"
            />
          </div>

          {/* Controls - Smaller block */}
          <div className="md:col-span-5 space-y-6">
            {/* Language Selector */}
            <div className="bg-white rounded-3xl border border-slate-100 shadow-xl shadow-indigo-100/20 p-6 md:p-8 animate-slide-up delay-200">
              <h2 className="text-lg font-extrabold text-slate-900 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
                <Languages className="w-5 h-5 inline-block mr-2 text-indigo-500" />
                Translate To
              </h2>
              <Select value={targetLanguage} onValueChange={setTargetLanguage}>
                <SelectTrigger data-testid="language-selector" className="w-full rounded-xl border-2 border-slate-100 bg-slate-50 h-12 text-base font-medium">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent className="rounded-xl max-h-64">
                  {LANGUAGES.map((lang) => (
                    <SelectItem key={lang} value={lang} className="text-base">
                      {lang}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                data-testid="translate-btn"
                onClick={handleTranslate}
                disabled={!imageBase64 || isTranslating}
                className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-6 font-bold text-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {isTranslating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Translating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Translate Image
                  </>
                )}
              </Button>
            </div>

            {/* Quick tips */}
            <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-3xl p-6 text-white animate-slide-up delay-300">
              <h3 className="font-extrabold text-lg mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Tips for best results
              </h3>
              <ul className="space-y-2 text-sm text-indigo-100">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 mt-1.5 flex-shrink-0" />
                  Ensure text is clearly visible and well-lit
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 mt-1.5 flex-shrink-0" />
                  Hold the camera steady for crisp capture
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 mt-1.5 flex-shrink-0" />
                  Works with printed text, signs, and handwriting
                </li>
              </ul>
            </div>

            {/* Learn section */}
            <div className="grid grid-cols-2 gap-3 animate-slide-up delay-400">
              <button
                data-testid="dashboard-flashcards-btn"
                onClick={() => navigate("/flashcards")}
                className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-indigo-100/10 p-4 hover:border-indigo-200 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 text-left"
              >
                <BookOpen className="w-6 h-6 text-indigo-500 mb-2" />
                <p className="font-bold text-sm text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>Flashcards</p>
                <p className="text-xs text-slate-400 mt-0.5">Review vocabulary</p>
              </button>
              <button
                data-testid="dashboard-quiz-btn"
                onClick={() => navigate("/quiz")}
                className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-indigo-100/10 p-4 hover:border-yellow-200 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 text-left"
              >
                <Trophy className="w-6 h-6 text-yellow-500 mb-2" />
                <p className="font-bold text-sm text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>Quiz</p>
                <p className="text-xs text-slate-400 mt-0.5">Test your knowledge</p>
              </button>
            </div>
          </div>
        </div>

        {/* Translation Result */}
        {translationResult && (
          <div className="mt-6 animate-slide-up">
            <TranslationResult
              result={translationResult}
              targetLanguage={targetLanguage}
              autoPlay
            />
          </div>
        )}
      </main>
    </div>
  );
}
